import React, { useState, useEffect } from 'react';
import { apiService, Driver } from '../services/api';
import {
  saveDriverDraft,
  loadDriverDraft,
  clearDriverDraft,
  markDriverFreshStart,
  clearDriverFreshFlag,
  isDriverFreshStart,
} from '../utils/driverFormPersistence';

interface AddDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddDriver: (driver: Driver) => void;
}

interface ValidationErrors {
  driverName?: string;
  email?: string;
  mobileNumber?: string;
  drivingLicense?: string;
  address?: string;
  dlFile?: string;
  profilePicture?: string;
}

const AddDriverModal: React.FC<AddDriverModalProps> = ({ isOpen, onClose, onAddDriver }) => {
  const [formData, setFormData] = useState({
    driverName: '',
    email: '',
    mobileNumber: '',
    drivingLicense: '',
    address: '',
    dlFile: null as File | null,
    profilePicture: null as File | null
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showRestore, setShowRestore] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);

  // Validation functions
  const validateDriverName = (name: string): string | undefined => {
    if (!name.trim()) {
      return 'Driver name is required';
    }
    if (name.trim().length < 2) {
      return 'Driver name must be at least 2 characters';
    }
    if (name.trim().length > 50) {
      return 'Driver name must be less than 50 characters';
    }
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
      return 'Driver name can only contain letters and spaces';
    }
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    if (email.trim().length > 100) {
      return 'Email must be less than 100 characters';
    }
    return undefined;
  };

  const validateMobileNumber = (mobile: string): string | undefined => {
    if (!mobile.trim()) {
      return 'Mobile number is required';
    }
    const cleanMobile = mobile.replace(/\D/g, '');
    if (cleanMobile.length !== 10) {
      return 'Mobile number must be exactly 10 digits';
    }
    return undefined;
  };

  const validateDrivingLicense = (license: string): string | undefined => {
    const raw = license.trim().toUpperCase();
    if (!raw) return 'Driving license is required';
    // Accept hyphen, space, or no separator by normalizing
    const normalized = raw.replace(/[\s-]/g, '');
    const dlPattern = /^[A-Z]{2}[0-9]{2}[0-9]{4}[0-9]{7}$/;
    if (!dlPattern.test(normalized)) {
      return 'Valid formats: DL-14-2023-1234567, DL 14 2023 1234567, or DL1420231234567';
    }
    return undefined;
  };

  const validateAddress = (address: string): string | undefined => {
    if (!address.trim()) {
      return 'Address is required';
    }
    if (address.trim().length < 10) {
      return 'Address must be at least 10 characters';
    }
    if (address.trim().length > 200) {
      return 'Address must be less than 200 characters';
    }
    return undefined;
  };


  const validateProfilePicture = (file: File | null): string | undefined => {
    // Profile picture is optional; validate only when provided
    if (!file) return undefined;
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      return 'Profile picture must be a JPG, JPEG, or PNG file';
    }
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return 'Profile picture size must be less than 5MB';
    }
    
    return undefined;
  };

  const validateDLFile = (file: File | null): string | undefined => {
    if (!file) return 'Driving license file is required';
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      return 'Driving license file must be a JPG, JPEG, or PNG file';
    }
    
    const maxSize = 5 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'Driving license file size must be less than 5MB';
    }
    
    return undefined;
  };

  // Load draft when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        driverName: '',
        email: '',
        mobileNumber: '',
        drivingLicense: '',
        address: '',
        dlFile: null,
        profilePicture: null
      });
      setProfilePicturePreview('');
      setValidationErrors({});
      setDraftData(null);
      setShowRestore(false);
      return;
    }
    
    // Reset form first
    setFormData({
      driverName: '',
      email: '',
      mobileNumber: '',
      drivingLicense: '',
        address: '',
        dlFile: null,
      profilePicture: null
    });
    setProfilePicturePreview('');
    setValidationErrors({});
    
    if (isDriverFreshStart()) return;

    const draft = loadDriverDraft();
    if (draft) {
      setDraftData(draft);
      setShowRestore(true);
    }
  }, [isOpen]);

  // Save draft on form changes
  useEffect(() => {
    if (!isOpen || showRestore) return;
    
    // Debounce save to avoid too many writes
    const timeoutId = setTimeout(() => {
      saveDriverDraft(formData);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, showRestore]);

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    errors.driverName = validateDriverName(formData.driverName);
    errors.email = validateEmail(formData.email);
    errors.mobileNumber = validateMobileNumber(formData.mobileNumber);
    errors.drivingLicense = validateDrivingLicense(formData.drivingLicense);
    errors.address = validateAddress(formData.address);
    errors.profilePicture = validateProfilePicture(formData.profilePicture);
    errors.dlFile = validateDLFile(formData.dlFile);
    
    setValidationErrors(errors);
    
    // Check if there are any errors
    return !Object.values(errors).some(error => error !== undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate form before submission
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      // Format mobile number to include +91 prefix
      let formattedMobileNumber = formData.mobileNumber.trim();
      
      // Remove any existing country code or + sign
      formattedMobileNumber = formattedMobileNumber.replace(/^\+91/, '').replace(/^91/, '');
      
      // Remove any non-digit characters
      formattedMobileNumber = formattedMobileNumber.replace(/\D/g, '');
      
      // Add +91 prefix
      formattedMobileNumber = '+91' + formattedMobileNumber;

      const driverData = {
        ...formData,
        // Normalize DL before submit
        drivingLicense: formData.drivingLicense.toUpperCase().replace(/[\s-]/g, ''),
        mobileNumber: formattedMobileNumber,
        dlFile: formData.dlFile || undefined,
        profilePicture: formData.profilePicture || undefined
      };
      const response = await apiService.addDriver(driverData);
      
      if (response.success && response.data) {
        onAddDriver(response.data);
        clearDriverDraft();
        clearDriverFreshFlag();
        setFormData({ driverName: '', email: '', mobileNumber: '', drivingLicense: '', address: '', dlFile: null, profilePicture: null });
        setProfilePicturePreview('');
        setValidationErrors({});
        setDraftData(null);
        setShowRestore(false);
        onClose();
      } else {
        setError(response.error || 'Failed to add driver');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Real-time validation handlers
  const handleDriverNameChange = (value: string) => {
    setFormData({ ...formData, driverName: value });
    const error = validateDriverName(value);
    setValidationErrors(prev => ({ ...prev, driverName: error }));
  };

  const handleEmailChange = (value: string) => {
    setFormData({ ...formData, email: value });
    const error = validateEmail(value);
    setValidationErrors(prev => ({ ...prev, email: error }));
  };

  const handleMobileNumberChange = (value: string) => {
    // Only allow digits and limit to 10 digits
    const cleanValue = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, mobileNumber: cleanValue });
    const error = validateMobileNumber(cleanValue);
    setValidationErrors(prev => ({ ...prev, mobileNumber: error }));
  };

  const handleDrivingLicenseChange = (value: string) => {
    // Keep spaces/dashes for user typing; validation normalizes
    const upperValue = value.toUpperCase();
    setFormData({ ...formData, drivingLicense: upperValue });
    const error = validateDrivingLicense(upperValue);
    setValidationErrors(prev => ({ ...prev, drivingLicense: error }));
  };

  const handleAddressChange = (value: string) => {
    setFormData({ ...formData, address: value });
    const error = validateAddress(value);
    setValidationErrors(prev => ({ ...prev, address: error }));
  };


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData({ ...formData, dlFile: file });
    const error = validateDLFile(file);
    setValidationErrors(prev => ({ ...prev, dlFile: error }));
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        setValidationErrors(prev => ({ ...prev, profilePicture: 'Please select a valid image file' }));
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        setValidationErrors(prev => ({ ...prev, profilePicture: 'Image size should be less than 5MB' }));
        return;
      }

      setFormData(prev => ({ ...prev, profilePicture: file }));
      setValidationErrors(prev => ({ ...prev, profilePicture: undefined }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, profilePicture: null }));
      setProfilePicturePreview('');
      setValidationErrors(prev => ({ ...prev, profilePicture: undefined }));
    }
  };

  const handleRestore = () => {
    if (draftData) {
      setFormData(prev => ({
        ...prev,
        ...draftData,
        dlFile: null, // Files cannot be restored
        profilePicture: null,
      }));
    }
    clearDriverFreshFlag();
    setShowRestore(false);
    setDraftData(null);
  };

  const handleStartFresh = () => {
    clearDriverDraft();
    markDriverFreshStart();
    setShowRestore(false);
    setDraftData(null);
    // Reset form to empty values
    setFormData({
      driverName: '',
      email: '',
      mobileNumber: '',
      drivingLicense: '',
        address: '',
        dlFile: null,
      profilePicture: null
    });
    setProfilePicturePreview('');
    setValidationErrors({});
  };

  const handleClose = () => {
    clearDriverFreshFlag();
    setShowRestore(false);
    setDraftData(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <React.Fragment>
      {showRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-semibold mb-2">Restore Form?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Unsaved form data was found. Do you want to restore it?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleStartFresh}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={handleRestore}
                className="px-4 py-2 bg-jd-green text-white rounded-md hover:bg-green-600 transition-colors"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Add Driver</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={() => {
              if (showRestore) return;
              handleClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={showRestore}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">

   {/* Profile Picture Upload */}
   <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                {profilePicturePreview ? (
                  <img 
                    src={profilePicturePreview} 
                    alt="Profile Preview" 
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-r from-jd-blue to-jd-green rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {formData.driverName ? formData.driverName.charAt(0).toUpperCase() : 'D'}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleProfilePictureChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-jd-blue file:text-white hover:file:bg-blue-700 file:cursor-pointer cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">
                  JPEG, JPG, and PNG. Max size 5MB.
                </p>
                {validationErrors.profilePicture && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.profilePicture}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driver Name</label>
            <input
              type="text"
              // required
              placeholder="Enter Driver Name"
              value={formData.driverName}
              onChange={(e) => handleDriverNameChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                validationErrors.driverName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {validationErrors.driverName && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.driverName}</p>
            )}
          </div>

       
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              // required
              placeholder="Enter Email Address"
              value={formData.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                validationErrors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {validationErrors.email && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 text-sm">+91</span>
              </div>
              <input
                type="tel"
                // required
                placeholder="Enter 10-digit mobile number"
                value={formData.mobileNumber}
                onChange={(e) => handleMobileNumberChange(e.target.value)}
                className={`w-full pl-12 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                  validationErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                maxLength={10}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              +91 will be automatically added to your number
            </p>
            {validationErrors.mobileNumber && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.mobileNumber}</p>
            )}
          </div>
          
         
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <input
              type="text"
              // required
              placeholder="Enter Address"
              value={formData.address}
              onChange={(e) => handleAddressChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                validationErrors.address ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {validationErrors.address && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driving License No</label>
            <input
              type="text"
              // required
              placeholder="Enter Driving License"
              value={formData.drivingLicense}
              onChange={(e) => handleDrivingLicenseChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                validationErrors.drivingLicense ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {validationErrors.drivingLicense && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.drivingLicense}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driving licence</label>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                Upload File
              </button>
              <div className={`flex-1 px-3 py-2 border rounded-lg bg-gray-50 flex items-center justify-between ${
                validationErrors.dlFile ? 'border-red-500' : 'border-gray-300'
              }`}>
                <span className="text-gray-500">
                  {formData.dlFile ? (formData.dlFile as File).name : 'File Name'}
                </span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              JPEG, JPG and PNG. Max size 5MB.
            </p>
            {validationErrors.dlFile && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.dlFile}</p>
            )}
            <input
              id="file-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={showRestore}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || showRestore}
              className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Adding...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </React.Fragment>
  );
};

export default AddDriverModal;
